import { SITE_URL } from '@/lib/seo';
import { localizePath } from '@/lib/localeRouting';
import type { JsonLd } from '@/lib/schema';
import type { ProjectCostProfile } from '@/lib/projectCostEstimate';

export type ProposalLang = 'tr' | 'en';

type Localized = { tr: string; en: string };

export const PROPOSAL_PROCESS_ANCHOR = 'proposal-process';
export const DELIVERY_TERMS_ANCHOR = 'delivery-terms';

export const PROPOSAL_PROCESS_COPY = {
  tag: { tr: 'Teklif süreci', en: 'Proposal process' },
  title: { tr: 'Teklifinizi nasıl hazırlıyoruz?', en: 'How we prepare your proposal' },
  intro: {
    tr: 'Sabit bir fiyat listemiz yok; fiyat her proje için ayrı belirlenir. Önce projeyi analiz ediyoruz, ardından kapsama uygun outsource ekip kurulur ve fiyat bu ekibin efor ve maliyetinden hesaplanır. Her proje aynı altı adımdan geçer; maliyet tahmincisindeki bantlar yalnızca ön fikir verir, kesin fiyat yazılı teklifte yer alır.',
    en: 'We do not work from a fixed price list; pricing is set per project. We analyse the project first, then an outsourced team is assembled for that scope, and the price is calculated from that team’s effort and cost. Every project goes through the same six steps; the cost estimator bands are indicative only, and the final price is documented in a written proposal.',
  },
  compactIntro: {
    tr: 'Formu gönderdikten sonra projenizi analiz eder, efora göre outsource ekibi planlar ve teklifinizi şu adımlarla hazırlarız:',
    en: 'After you send the form, we analyse your project, plan the outsourced team from the effort, and prepare your proposal in these steps:',
  },
  estimatorLink: { tr: 'Örnek bütçe bantları: proje maliyet tahmincisi', en: 'Indicative budget bands: project cost estimator' },
  processLink: { tr: 'Teklif sürecimizin 6 adımı', en: 'Our 6-step proposal process' },
} satisfies Record<string, Localized>;

/** Who and where we serve; deliberately no per-city or per-sector landing pages. */
export const SERVICE_COVERAGE_COPY = {
  title: { tr: 'Hangi işleri, nerede yapıyoruz?', en: 'What we build, and where' },
  body: {
    tr: 'Her sektörde, her türlü yazılım işini üstleniyoruz: web, mobil, SaaS, AI, entegrasyon ve kurum içi sistemler. Merkezimiz İstanbul’da; Türkiye’nin tüm şehirlerindeki ve yurt dışındaki müşterilerimize uzaktan hizmet veriyoruz.',
    en: 'We take on every kind of software work in every sector: web, mobile, SaaS, AI, integrations, and internal systems. We are based in Istanbul and work remotely with clients in every city in Turkey and in other countries.',
  },
} satisfies Record<string, Localized>;

export const PROPOSAL_PROCESS_STEPS: { title: Localized; description: Localized }[] = [
  {
    title: { tr: 'Talebinizi dinliyoruz', en: 'We listen to your request' },
    description: {
      tr: 'İş hedefinizi, kullanıcılarınızı, mevcut sistemlerinizi, takvim ve bütçe beklentinizi dinliyor; varsa doküman, tasarım ve örnekleri inceliyoruz.',
      en: 'We listen to your business goal, users, existing systems, timeline and budget expectations, and review any documents, designs, or examples you share.',
    },
  },
  {
    title: { tr: 'Kapsamı ve iş listesini çıkarıyoruz', en: 'We define the scope and task list' },
    description: {
      tr: 'Talebi ekran, akış, entegrasyon ve teknik bileşen düzeyinde iş kalemlerine ayırıyor; kapsam içi ve kapsam dışı maddeleri yazılı hale getiriyoruz.',
      en: 'We break the request into tasks at the level of screens, flows, integrations, and technical components, and write down what is in and out of scope.',
    },
  },
  {
    title: { tr: 'Eforu hesaplıyoruz', en: 'We estimate the effort' },
    description: {
      tr: 'Her iş kalemi için tasarım, geliştirme, test ve yayın eforunu tahmin ediyor; belirsiz noktaları varsayım olarak işaretliyoruz.',
      en: 'We estimate design, development, testing, and release effort for each task and flag open questions as explicit assumptions.',
    },
  },
  {
    title: { tr: 'Projeye özel ekibi kuruyoruz', en: 'We assemble the project team' },
    description: {
      tr: 'Kapsama göre gereken rolleri (ör. ürün tasarımı, frontend, backend, mobil, AI, QA) belirliyor ve projeye özel outsource ekip kuruyoruz; ekip büyüklüğü efor hesabına göre şekillenir.',
      en: 'Based on the scope, we decide which roles the project needs (e.g. product design, frontend, backend, mobile, AI, QA) and assemble an outsourced team for it; team size follows the effort estimate.',
    },
  },
  {
    title: { tr: 'Fiyatı hesaplıyor, teklifi dokümante ediyoruz', en: 'We calculate the price and document the proposal' },
    description: {
      tr: 'Fiyatı, efor ve kurulan ekibin maliyetinden hesaplıyor; kapsamı, varsayımları, takvimi, fiyatı ve devir koşullarını içeren yazılı teklifi hazırlıyoruz.',
      en: 'We calculate the price from the effort and the cost of the assembled team and prepare a written proposal covering scope, assumptions, timeline, price, and handover terms.',
    },
  },
  {
    title: { tr: 'Gerekirse teknik doküman ekliyoruz', en: 'We add a technical document when needed' },
    description: {
      tr: 'Gereksinimler gerektiriyorsa mimari, entegrasyon ve veri akışını anlatan bir teknik doküman hazırlayıp teklifle birlikte iletiyoruz.',
      en: 'When the requirements call for it, we prepare a technical document describing architecture, integrations, and data flow, and deliver it together with the proposal.',
    },
  },
];

export const DELIVERY_TERMS_COPY = {
  title: { tr: 'Süre, fiyat ve devir koşulları', en: 'Timeline, pricing, and handover terms' },
  intro: {
    tr: 'Teslim süresi, fiyat ve devir koşulları projeye özeldir: proje analizinden sonra kurulan outsource ekibin efor ve maliyetine göre yazılı teklifte belirlenir. Karar vermeden önce bunları neyin etkilediğini ve devirde genellikle nelerin yer aldığını aşağıda özetliyoruz.',
    en: 'Delivery timelines, pricing, and handover terms are project-specific: after the project analysis they are set in the written proposal from the effort and cost of the outsourced team assembled for it. Below is what influences them and what a handover typically includes, so you can judge fit before you ask for a quote.',
  },
  factorsTitle: { tr: 'Süreyi ve fiyatı etkileyenler', en: 'What drives timeline and price' },
  bandsTitle: { tr: 'Başlangıç referansı', en: 'Starting reference' },
  bandsIntro: {
    tr: 'Ücretsiz maliyet tahmincisindeki örnek bantlar (TRY, KDV hariç) ilk fikir içindir; kesin rakam teklif sürecinde çıkar. Örnek profil:',
    en: 'The sample bands in our free cost estimator (TRY, excl. VAT) are for orientation; the final figure comes out of the proposal process. Sample profile:',
  },
  handoverTitle: { tr: 'Devirde genellikle neler var?', en: 'What a handover typically includes' },
  handoverIntro: {
    tr: 'Kesin devir kapsamı teklifte tanımlanır; genellikle şunları içerir:',
    en: 'The exact handover scope is defined in each proposal; it typically includes:',
  },
  supportNote: {
    tr: 'Yayın sonrası bakım ve destek ayrıca kapsamlanır; süresi, kapsamı ve yanıt şekli standart bir SLA yerine teklifte projeye özel olarak yazılır.',
    en: 'Post-launch maintenance and support are scoped separately; their duration, coverage, and response model are written into the proposal per project rather than offered as a standard SLA.',
  },
} satisfies Record<string, Localized>;

export const DELIVERY_TERM_FACTORS: Localized[] = [
  {
    tr: 'Kapsam: ekran, kullanıcı rolü ve iş akışı sayısı; MVP mi, genişletilmiş platform mu',
    en: 'Scope: number of screens, user roles, and workflows; MVP or expanded platform',
  },
  {
    tr: 'Entegrasyonlar: ödeme, CRM/ERP, harici API’ler ve veri taşıma ihtiyacı',
    en: 'Integrations: payments, CRM/ERP, third-party APIs, and data migration',
  },
  {
    tr: 'Ekip: gereken roller ve paralel çalışacak kişi sayısı',
    en: 'Team: required roles and how many people work in parallel',
  },
  {
    tr: 'Uyumluluk ve güvenlik: KVKK/GDPR, yetkilendirme, denetim kaydı ve sektör gereksinimleri',
    en: 'Compliance and security: KVKK/GDPR, permissions, audit logging, and industry requirements',
  },
  {
    tr: 'Mevcut varlıklar: hazır tasarım, kod tabanı veya altyapının olup olmaması',
    en: 'Existing assets: whether designs, a codebase, or infrastructure already exist',
  },
];

export const HANDOVER_ITEMS: Localized[] = [
  { tr: 'Kaynak kod ve depo erişimi', en: 'Source code and repository access' },
  { tr: 'Teknik ve kullanım dokümantasyonu', en: 'Technical and usage documentation' },
  {
    tr: 'Hosting, alan adı ve üçüncü taraf servis hesaplarının erişim devri',
    en: 'Access transfer for hosting, domain, and third-party service accounts',
  },
  { tr: 'Ekibinizle bilgi aktarımı oturumu', en: 'A knowledge transfer session with your team' },
];

const SERVICE_COST_PROFILE: Record<string, ProjectCostProfile> = {
  'saas-mvp-development': 'saas_mvp',
  'startup-product-engineering': 'saas_mvp',
  'mobile-app-development': 'mobile_product',
  'web-development': 'marketing_site',
  'custom-software-development': 'general',
};

export function getCostProfileForService(serviceSlug: string): ProjectCostProfile | null {
  return SERVICE_COST_PROFILE[serviceSlug] ?? null;
}

export type AutomationToolExample = {
  toolId: string;
  automates: Localized;
  inputs: Localized;
  outputs: Localized;
  flow: Localized;
  safeguards: Localized;
};

export const AUTOMATION_EXAMPLES_COPY = {
  title: { tr: 'Otomasyon örnekleri: kendi araçlarımız', en: 'Automation examples: our own tools' },
  intro: {
    tr: 'Aşağıdakiler müşteri projesi değil; figtures.com’da yayında olan, kendi geliştirdiğimiz ücretsiz araçlar. Her biri girdi → işleme → çıktı akışını ve hata/onay adımını açıkça gösterir.',
    en: 'These are not client projects; they are free tools we built and run on figtures.com. Each one shows an input → processing → output flow and its error or approval step.',
  },
  automatesLabel: { tr: 'Ne otomatikleştirir', en: 'What it automates' },
  inputsLabel: { tr: 'Girdi', en: 'Input' },
  outputsLabel: { tr: 'Çıktı', en: 'Output' },
  flowLabel: { tr: 'Veri akışı', en: 'Data flow' },
  safeguardsLabel: { tr: 'Hata / onay', en: 'Errors / approval' },
  openTool: { tr: 'Aracı aç', en: 'Open the tool' },
} satisfies Record<string, Localized>;

export const AUTOMATION_TOOL_EXAMPLES: AutomationToolExample[] = [
  {
    toolId: 'project-cost-estimator',
    automates: {
      tr: 'Serbest metin proje tanımını beş profilden birine sınıflandırır ve örnek bütçe/süre bantlarını üretir; sonucu teklif formuna taşır.',
      en: 'Classifies a free-text project description into one of five profiles, produces sample budget and timeline bands, and carries the result into the quote form.',
    },
    inputs: { tr: 'Proje türü (2–120 karakter)', en: 'Project type (2–120 characters)' },
    outputs: {
      tr: 'MVP, platform, UI/UX ve bakım için TRY bantları (KDV hariç), metodoloji notu ve ilgili hizmet linkleri',
      en: 'TRY bands (excl. VAT) for MVP, platform, UI/UX, and maintenance, plus a methodology note and related service links',
    },
    flow: {
      tr: 'Hesap tarayıcıda kural tabanlı yapılır, sunucuya istek gitmez. “Teklif iste” ile tahmin özeti oturum deposu (sessionStorage) üzerinden iletişim formuna aktarılır; URL’de yalnızca araç ve proje tipi kimliği taşınır.',
      en: 'The calculation runs rule-based in the browser with no server call. “Request a quote” passes the estimate summary to the contact form via sessionStorage; only the tool and project-type IDs travel in the URL.',
    },
    safeguards: {
      tr: 'Zorunlu alan boşsa veya karakter sınırı aşılırsa hesap butonu pasif kalır; sonuç sabit fiyat değil, teklif sürecinin girdisidir.',
      en: 'The calculate button stays disabled while the required field is empty or over the limit; the output is an input to the proposal process, not a fixed price.',
    },
  },
  {
    toolId: 'roi-calculator',
    automates: {
      tr: 'Aylık pazarlama bütçesinden kanal bazında tahmini getiri senaryosu çıkarır.',
      en: 'Turns a monthly marketing budget into a channel-by-channel estimated return scenario.',
    },
    inputs: { tr: 'Aylık bütçe (TL)', en: 'Monthly budget (TRY)' },
    outputs: {
      tr: 'E-posta, sosyal medya reklamı ve SEO için tahmini getiri satırları ve toplam tahmin',
      en: 'Estimated return lines for email, social ads, and SEO, plus a total estimate',
    },
    flow: {
      tr: 'Tarayıcıda sabit kanal katsayılarıyla hesaplanır; veri sunucuya gönderilmez.',
      en: 'Calculated in the browser with fixed channel multipliers; no data is sent to a server.',
    },
    safeguards: {
      tr: 'Rakam dışı karakterler ayıklanır; sonuçlar varsayıma dayalı tahmindir, kampanya garantisi değildir.',
      en: 'Non-numeric characters are stripped; results are assumption-based estimates, not campaign guarantees.',
    },
  },
  {
    toolId: 'quote-proposal-writer',
    automates: {
      tr: 'Hizmet, kapsam ve bütçe bilgisinden teklif mektubu taslağı hazırlar.',
      en: 'Drafts a proposal letter from service, scope, and budget details.',
    },
    inputs: {
      tr: 'Hizmet / proje türü, kapsam detayları, bütçe aralığı, müşteri profili',
      en: 'Service / project type, scope details, budget range, client profile',
    },
    outputs: {
      tr: 'Teklif mektubu, kapsam tablosu ve ödeme planı taslağı',
      en: 'Proposal letter, scope table, and payment plan draft',
    },
    flow: {
      tr: 'Girdiler tarayıcıda doğrulanır, sunucusuz bir API fonksiyonuna gönderilir ve Gemini modeliyle yapılandırılmış metin olarak döner.',
      en: 'Inputs are validated in the browser, sent to a serverless API function, and returned as structured text generated with a Gemini model.',
    },
    safeguards: {
      tr: 'Bağlantı hatası, istek limiti (HTTP 429) veya boş yanıtta anlaşılır bir hata mesajı gösterilir ve yeniden denenebilir. Çıktı taslaktır; KDV, imza ve sözleşme koşulları gönderilmeden önce kullanıcı tarafından tamamlanır.',
      en: 'Connection errors, rate limits (HTTP 429), or empty responses show a clear error message and can be retried. Output is a draft; VAT, signature, and contract terms are completed by the user before sending.',
    },
  },
  {
    toolId: 'customer-reply-generator',
    automates: {
      tr: 'Gelen müşteri mesajına amaç ve kanala uygun yanıt seçenekleri üretir.',
      en: 'Generates reply options for an incoming customer message, matched to intent and channel.',
    },
    inputs: {
      tr: 'Gelen mesaj, mesaj amacı (soru, satış, şikayet, randevu, iade), kanal (WhatsApp, e-posta, Instagram DM)',
      en: 'Incoming message, intent (question, sales, complaint, booking, return), channel (WhatsApp, email, Instagram DM)',
    },
    outputs: { tr: 'Üç farklı tonda yanıt metni', en: 'Reply text in three different tones' },
    flow: {
      tr: 'Mesaj ve seçimler sunucusuz API fonksiyonuna gönderilir, Gemini modeliyle üretilen yanıtlar sayfada listelenir.',
      en: 'The message and selections go to the serverless API function; replies generated with a Gemini model are listed on the page.',
    },
    safeguards: {
      tr: 'Araç mesaj göndermez: kullanıcı yanıtı seçer, düzenler ve kendi kanalından iletir (insan onayı). Hata durumunda yeniden deneme sunulur.',
      en: 'The tool never sends messages: the user picks, edits, and sends the reply from their own channel (human approval). Errors offer a retry.',
    },
  },
];

export function localizeProposal(text: Localized, lang: ProposalLang): string {
  return text[lang];
}

export function getProposalProcessUrl(lang: ProposalLang): string {
  return `${SITE_URL}${localizePath('/services', lang)}#${PROPOSAL_PROCESS_ANCHOR}`;
}

/** Plain ItemList (no HowTo): describes the steps without targeting a rich result. */
export function buildProposalProcessItemListSchema(lang: ProposalLang): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': getProposalProcessUrl(lang),
    name: PROPOSAL_PROCESS_COPY.title[lang],
    description: PROPOSAL_PROCESS_COPY.intro[lang],
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: PROPOSAL_PROCESS_STEPS.length,
    itemListElement: PROPOSAL_PROCESS_STEPS.map((step, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: step.title[lang],
      description: step.description[lang],
    })),
  };
}
